import VimeoPlayer from "@vimeo/player";
import PlayerStates from "youtube-player/dist/constants/PlayerStates";
import { YouTubePlayer } from "youtube-player/dist/types";
import { acceptTargetingConsent, hasTargetingConsent } from "../Utilities/cookieConsent";
import { prefersReducedMotion } from "../Utilities/userPreferences";
import {
  getPlayer,
  isVimeoPlayer,
  isYouTubePlayer,
  isYouTubeUrl,
  setVideoDurationStructuredData
} from "../Utilities/video";

const playYouTubeVideo = async (player: YouTubePlayer) => {
  const state = await player.getPlayerState();

  if (state !== PlayerStates.PLAYING) {
    await player.playVideo();
  }
};

const playVimeoVideo = async (player: VimeoPlayer) => {
  if (await player.getPaused()) {
    await player.play();
  }
};

export const playVideo = async (player: VimeoPlayer | YouTubePlayer) => {
  if (isYouTubePlayer(player)) {
    await playYouTubeVideo(player);
  }

  if (isVimeoPlayer(player)) {
    await playVimeoVideo(player);
  }
};

export const pauseVideo = async (player: VimeoPlayer | YouTubePlayer) => {
  if (isYouTubePlayer(player)) {
    if ((await player.getPlayerState()) == PlayerStates.PAUSED) {
      console.info("The YouTube video is already paused");
      return;
    }

    await player.pauseVideo();
    console.info("The YouTube video paused");
  }

  if (isVimeoPlayer(player)) {
    if (await player.getPaused()) {
      console.info("The Vimeo video is already paused");
      return;
    }

    await player.pause();
    console.info("The Vimeo video paused");
  }
};

const hidePreviewButton = (videoPreviewButton: HTMLElement) => {
  videoPreviewButton.inert = true;
};

document.querySelectorAll<HTMLElement>("[data-video]").forEach(async videoElement => {
  const videoPreviewButton = videoElement.querySelector(
    "[data-video-preview-button]"
  ) as HTMLButtonElement;

  if (!videoPreviewButton) {
    console.info("A video without a preview button - skipping");
    return;
  }

  const videoIframe = videoElement.querySelector("iframe");

  if (!videoIframe) {
    console.error("A video iframe missing!");
    return;
  }

  const isYouTube = isYouTubeUrl(videoIframe.src);
  const consentWarning = videoElement.querySelector<HTMLElement>(
    "[data-consent-warning]"
  );
  const hasPreview = videoElement.dataset.hasPreview === "true";
  const autoplay = videoElement.dataset.autoplay === "true";

  let player: ReturnType<typeof getPlayer>;

  if (!isYouTube) {
    player = getPlayer(videoIframe);
    if (!player) {
      console.error("Couldn't get a video player");
      return;
    }
    await setVideoDurationStructuredData(player);
  }

  const showConsentWarning = () => {
    hidePreviewButton(videoPreviewButton);
    if (consentWarning) {
      consentWarning.inert = false;
    }
  };

  const initYouTubeAndPlay = async () => {
    if (consentWarning) {
      consentWarning.inert = true;
    }

    if (!player) {
      player = getPlayer(videoIframe);
      if (player) {
        setVideoDurationStructuredData(player).catch(console.warn);
      }
    }
    if (player) {
      await playVideo(player);
    }
  };

  // YouTube with no preview image and no consent: show warning immediately
  if (isYouTube && !hasPreview && !hasTargetingConsent()) {
    showConsentWarning();
  }

  videoPreviewButton.onclick = async () => {
    if (isYouTube) {
      if (hasTargetingConsent()) {
        hidePreviewButton(videoPreviewButton);
        await initYouTubeAndPlay();
      } else {
        showConsentWarning();
      }
    } else {
      hidePreviewButton(videoPreviewButton);
      if (player) {
        await playVideo(player);
      }
    }
  };

  consentWarning
    ?.querySelector<HTMLElement>("[data-accept-targeted-cookies]")
    ?.addEventListener("click", async () => {
      acceptTargetingConsent();
      await initYouTubeAndPlay();
    });

  // Autoplay when scrolled into view (YouTube only allowed if consent already given)
  if (autoplay && !prefersReducedMotion() && (!isYouTube || hasTargetingConsent())) {
    const scrolledIntoViewHandler = async (entries: IntersectionObserverEntry[]) => {
      if (entries.some(entry => entry.isIntersecting)) {
        hidePreviewButton(videoPreviewButton);
        if (isYouTube) {
          await initYouTubeAndPlay();
        } else if (player) {
          await playVideo(player);
        }
      }
    };

    const intersectionObserver = new IntersectionObserver(scrolledIntoViewHandler, {
      threshold: 0
    });

    intersectionObserver.observe(videoIframe);
  }
});
